import React, { useEffect, useState } from 'react';
import { AuthPageLayout } from 'components/layout';
import { FormattedMessage, useIntl } from 'react-intl';
import { Schemas, useFormValidation } from 'utils/validation';
import { Services } from 'services';
import { useLocation } from 'react-router-dom';
import { Box, Button, TextField } from '@clariness/clarikit-react';
import { captureEventV2 } from 'utils/events/captureEvent';
import { Events } from '@clariness/tracking-event-runtime';

const EXPIRED_SEARCH = '?expired=true';

type ForgotPasswordForm = {
  email: string;
};

export const ForgotPassword = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useFormValidation<ForgotPasswordForm>();

  const [passwordExpired, setPasswordExpired] = useState(false);

  const intl = useIntl();
  const location = useLocation();
  const emailLabel = intl.formatMessage({
    id: 'is.forgot_password.email_input_placeholder',
    defaultMessage: 'Email',
  });
  const emailRegistration = register('email', Schemas.email(intl));

  useEffect(() => {
    if (location?.search === EXPIRED_SEARCH) {
      setPasswordExpired(true);
    }
  }, [location]);

  const { sendForgotPasswordEmail, isLoading } =
    Services.SiteEmployees.useSendForgotPasswordEmail();

  return (
    <AuthPageLayout>
      <AuthPageLayout.Logo />

      <AuthPageLayout.Heading>
        {passwordExpired ? (
          <FormattedMessage
            id="is.forgot_password.password_expired"
            defaultMessage="Password Expired"
          />
        ) : (
          <FormattedMessage
            id="is.forgot_password.heading"
            defaultMessage="Forgotten Password"
          />
        )}
      </AuthPageLayout.Heading>

      <AuthPageLayout.Paragraph>
        {passwordExpired ? (
          <FormattedMessage
            id="is.forgot_password.expired_password_info"
            defaultMessage="Your password has expired and must be changed."
          />
        ) : (
          <FormattedMessage
            id="is.forgot_password.main_paragraph"
            defaultMessage="Confirm the email address associated with your account, and we'll send you a link to reset your password"
          />
        )}
      </AuthPageLayout.Paragraph>

      <AuthPageLayout.Form
        onSubmit={handleSubmit(({ email }) => {
          sendForgotPasswordEmail(email);
          captureEventV2(
            Events.InvestigatorService.InvestigatorForgotPassword({})
          );
        })}
      >
        <Box width="100%">
          <TextField
            name={emailRegistration.name}
            ref={emailRegistration.ref}
            placeholder={emailLabel}
            value={watch('email')}
            onChange={(value) => {
              emailRegistration.onChange({
                target: { name: emailRegistration.name, value },
              });
            }}
            onBlur={(event) => {
              if (event) {
                emailRegistration.onBlur(event);
              }
            }}
            error={errors?.email?.message}
            data-test-id="IS-forgot-password-email-input"
            autoComplete="email"
            label={emailLabel}
            labelHidden
          />
        </Box>
        <Button
          submit
          size="l"
          variant="primary"
          colorVariant="green"
          loading={isLoading}
          data-test-id="IS-forgot-password-send-link-button"
        >
          {intl.formatMessage({
            id: 'is.forgot_password.send_button',
            defaultMessage: 'Send link',
          })}
        </Button>
      </AuthPageLayout.Form>

      <AuthPageLayout.InfoLinks />
      <AuthPageLayout.Footer />
    </AuthPageLayout>
  );
};
